import { randomBase32String } from 'js/utils/string';

/** Same length as `addQuestionSet` / `addQuestionToSet` in doc-utils and capability element actions. */
const RUBRIC_GUID_LENGTH = 10;

export function generateRubricGuid() {
  return randomBase32String(RUBRIC_GUID_LENGTH);
}

/**
 * Ensures every question set and question has a stable `guid` (base32, same family as assessment editors).
 * Missing guids are filled once; existing values are preserved.
 *
 * @param {object|null|undefined} document
 * @returns {object}
 */
export function ensureRubricDocumentGuids(document) {
  if (!document || typeof document !== 'object') {
    return document ?? {};
  }
  const questionSets = document.question_sets;
  if (!Array.isArray(questionSets)) {
    return document;
  }
  const nextSets = questionSets.map((qs) => {
    const setGuid = qs.guid || generateRubricGuid();
    const questions = qs.questions;
    const nextQuestions = Array.isArray(questions)
      ? questions.map((q) => ({
          ...q,
          guid: q.guid || generateRubricGuid(),
          sources: Array.isArray(q.sources)
            ? q.sources.map((src) => ({
                ...src,
                guid: src.guid || generateRubricGuid(),
              }))
            : q.sources,
        }))
      : questions;
    return { ...qs, guid: setGuid, questions: nextQuestions };
  });
  return { ...document, question_sets: nextSets };
}

/**
 * @param {object|null} rubric
 * @returns {object|null}
 */
export function ensureRubricGuids(rubric) {
  if (!rubric) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const nextDoc = ensureRubricDocumentGuids(doc);
  return { ...rubric, document: nextDoc };
}

function updateQuestionSetInDocument(
  doc,
  questionSetGuid,
  fieldName,
  rawValue
) {
  const value = rawValue;
  const questionSets = doc?.question_sets;
  if (!Array.isArray(questionSets)) {
    return { ...doc, question_sets: [] };
  }
  const idx = questionSets.findIndex((qs) => qs.guid === questionSetGuid);
  if (idx === -1) {
    return doc;
  }
  const nextSets = [...questionSets];
  nextSets[idx] = { ...nextSets[idx], [fieldName]: value };
  return { ...doc, question_sets: nextSets };
}

function updateQuestionInDocument(
  doc,
  questionSetGuid,
  questionGuid,
  fieldName,
  rawValue
) {
  const value = rawValue;
  const questionSets = doc?.question_sets;
  if (!Array.isArray(questionSets)) {
    return doc;
  }
  const sIdx = questionSets.findIndex((qs) => qs.guid === questionSetGuid);
  if (sIdx === -1) {
    return doc;
  }
  const qs = questionSets[sIdx];
  const questions = qs.questions;
  if (!Array.isArray(questions)) {
    return doc;
  }
  const qIdx = questions.findIndex((q) => q.guid === questionGuid);
  if (qIdx === -1) {
    return doc;
  }
  const nextQuestions = [...questions];
  nextQuestions[qIdx] = {
    ...nextQuestions[qIdx],
    [fieldName]: value,
  };
  const nextSets = [...questionSets];
  nextSets[sIdx] = { ...qs, questions: nextQuestions };
  return { ...doc, question_sets: nextSets };
}

/**
 * @param {object|null} rubric
 * @returns {object|null}
 */
export function updateQuestionSetField(
  rubric,
  questionSetGuid,
  fieldName,
  value
) {
  if (!rubric) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const nextDoc = updateQuestionSetInDocument(
    doc,
    questionSetGuid,
    fieldName,
    value
  );
  return { ...rubric, document: nextDoc };
}

/**
 * @param {object|null} rubric
 * @returns {object|null}
 */
export function updateQuestionField(
  rubric,
  questionSetGuid,
  questionGuid,
  fieldName,
  value
) {
  if (!rubric) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const nextDoc = updateQuestionInDocument(
    doc,
    questionSetGuid,
    questionGuid,
    fieldName,
    value
  );
  return { ...rubric, document: nextDoc };
}

function cloneQuestionSets(doc) {
  return (doc.question_sets ?? []).map((s) => ({
    ...s,
    questions: [...(s.questions ?? [])],
  }));
}

/**
 * Re-order question sets only (same pattern as capability pages).
 */
export function moveRubricQuestionSetBefore(rubric, draggedGuid, beforeGuid) {
  if (!rubric || draggedGuid === beforeGuid) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const sets = [...(doc.question_sets ?? [])];
  const fromIdx = sets.findIndex((s) => s.guid === draggedGuid);
  const beforeIdx = sets.findIndex((s) => s.guid === beforeGuid);
  if (fromIdx === -1 || beforeIdx === -1) {
    return rubric;
  }
  const [item] = sets.splice(fromIdx, 1);
  let target = beforeIdx;
  if (fromIdx < beforeIdx) {
    target -= 1;
  }
  sets.splice(target, 0, item);
  return { ...rubric, document: { ...doc, question_sets: sets } };
}

export function moveRubricQuestionSetAfter(rubric, draggedGuid, afterGuid) {
  if (!rubric) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const sets = [...(doc.question_sets ?? [])];
  const fromIdx = sets.findIndex((s) => s.guid === draggedGuid);
  const afterIdx = sets.findIndex((s) => s.guid === afterGuid);
  if (fromIdx === -1 || afterIdx === -1) {
    return rubric;
  }
  const [item] = sets.splice(fromIdx, 1);
  let target = afterIdx + 1;
  if (fromIdx <= afterIdx) {
    target -= 1;
  }
  sets.splice(target, 0, item);
  return { ...rubric, document: { ...doc, question_sets: sets } };
}

function moveRubricQuestionRelative(
  rubric,
  draggedGuid,
  fromSetGuid,
  toSetGuid,
  targetQuestionGuid,
  placement
) {
  if (!rubric || draggedGuid === targetQuestionGuid) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const sets = cloneQuestionSets(doc);

  const fromSi = sets.findIndex((s) => s.guid === fromSetGuid);
  if (fromSi === -1) {
    return rubric;
  }
  const fromQs = sets[fromSi].questions;
  const qFromIdx = fromQs.findIndex((q) => q.guid === draggedGuid);
  if (qFromIdx === -1) {
    return rubric;
  }

  const [moved] = fromQs.splice(qFromIdx, 1);
  sets[fromSi] = { ...sets[fromSi], questions: [...fromQs] };

  const toSi = sets.findIndex((s) => s.guid === toSetGuid);
  if (toSi === -1) {
    return rubric;
  }
  const toQs = [...(sets[toSi].questions ?? [])];
  const targetIdx = toQs.findIndex((q) => q.guid === targetQuestionGuid);
  if (targetIdx === -1) {
    return rubric;
  }

  const insertIdx = placement === 'after' ? targetIdx + 1 : targetIdx;
  toQs.splice(insertIdx, 0, moved);
  sets[toSi] = { ...sets[toSi], questions: toQs };

  return { ...rubric, document: { ...doc, question_sets: sets } };
}

export function moveRubricQuestionBefore(
  rubric,
  draggedGuid,
  fromSetGuid,
  toSetGuid,
  targetQuestionGuid
) {
  return moveRubricQuestionRelative(
    rubric,
    draggedGuid,
    fromSetGuid,
    toSetGuid,
    targetQuestionGuid,
    'before'
  );
}

export function moveRubricQuestionAfter(
  rubric,
  draggedGuid,
  fromSetGuid,
  toSetGuid,
  targetQuestionGuid
) {
  return moveRubricQuestionRelative(
    rubric,
    draggedGuid,
    fromSetGuid,
    toSetGuid,
    targetQuestionGuid,
    'after'
  );
}

/** Move a question to the end of another set (e.g. empty set). */
export function moveRubricQuestionToEnd(
  rubric,
  draggedGuid,
  fromSetGuid,
  toSetGuid
) {
  if (!rubric) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const sets = cloneQuestionSets(doc);

  const fromSi = sets.findIndex((s) => s.guid === fromSetGuid);
  if (fromSi === -1) {
    return rubric;
  }
  const fromQs = sets[fromSi].questions;
  const qFromIdx = fromQs.findIndex((q) => q.guid === draggedGuid);
  if (qFromIdx === -1) {
    return rubric;
  }

  const [moved] = fromQs.splice(qFromIdx, 1);
  sets[fromSi] = { ...sets[fromSi], questions: [...fromQs] };

  const toSi = sets.findIndex((s) => s.guid === toSetGuid);
  if (toSi === -1) {
    return rubric;
  }
  const toQs = [...(sets[toSi].questions ?? []), moved];
  sets[toSi] = { ...sets[toSi], questions: toQs };

  return { ...rubric, document: { ...doc, question_sets: sets } };
}

function moveRubricSourceRelative(
  rubric,
  questionSetGuid,
  questionGuid,
  draggedSourceGuid,
  targetSourceGuid,
  placement
) {
  if (!rubric || draggedSourceGuid === targetSourceGuid) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const sets = (doc.question_sets ?? []).map((qs) => ({
    ...qs,
    questions: (qs.questions ?? []).map((q) => ({
      ...q,
      sources: [...(q.sources ?? [])],
    })),
  }));

  const setIdx = sets.findIndex((s) => s.guid === questionSetGuid);
  if (setIdx === -1) {
    return rubric;
  }
  const questions = sets[setIdx].questions;
  const qIdx = questions.findIndex((q) => q.guid === questionGuid);
  if (qIdx === -1) {
    return rubric;
  }

  const sources = [...(questions[qIdx].sources ?? [])];
  const fromIdx = sources.findIndex((s) => s.guid === draggedSourceGuid);
  if (fromIdx === -1) {
    return rubric;
  }

  const [moved] = sources.splice(fromIdx, 1);
  const targetIdx = sources.findIndex((s) => s.guid === targetSourceGuid);
  if (targetIdx === -1) {
    return rubric;
  }

  const insertIdx = placement === 'after' ? targetIdx + 1 : targetIdx;
  sources.splice(insertIdx, 0, moved);

  const nextQuestions = [...questions];
  nextQuestions[qIdx] = { ...questions[qIdx], sources };
  const nextSets = [...sets];
  nextSets[setIdx] = { ...sets[setIdx], questions: nextQuestions };

  return { ...rubric, document: { ...doc, question_sets: nextSets } };
}

export function moveRubricSourceBefore(
  rubric,
  questionSetGuid,
  questionGuid,
  draggedSourceGuid,
  targetSourceGuid
) {
  return moveRubricSourceRelative(
    rubric,
    questionSetGuid,
    questionGuid,
    draggedSourceGuid,
    targetSourceGuid,
    'before'
  );
}

export function moveRubricSourceAfter(
  rubric,
  questionSetGuid,
  questionGuid,
  draggedSourceGuid,
  targetSourceGuid
) {
  return moveRubricSourceRelative(
    rubric,
    questionSetGuid,
    questionGuid,
    draggedSourceGuid,
    targetSourceGuid,
    'after'
  );
}

export function removeRubricSource(
  rubric,
  questionSetGuid,
  questionGuid,
  sourceGuid
) {
  if (!rubric || !sourceGuid) {
    return rubric;
  }

  const doc = rubric.document ?? {};
  const sets = (doc.question_sets ?? []).map((qs) => ({
    ...qs,
    questions: (qs.questions ?? []).map((q) => ({
      ...q,
      sources: [...(q.sources ?? [])],
    })),
  }));

  const setIdx = sets.findIndex((s) => s.guid === questionSetGuid);
  if (setIdx === -1) {
    return rubric;
  }

  const questions = sets[setIdx].questions;
  const qIdx = questions.findIndex((q) => q.guid === questionGuid);
  if (qIdx === -1) {
    return rubric;
  }

  const q = questions[qIdx];
  const nextSources = (q.sources ?? []).filter((s) => s.guid !== sourceGuid);
  if (nextSources.length === (q.sources ?? []).length) {
    return rubric;
  }

  const nextQuestions = [...questions];
  nextQuestions[qIdx] = { ...q, sources: nextSources };
  const nextSets = [...sets];
  nextSets[setIdx] = { ...sets[setIdx], questions: nextQuestions };

  return {
    ...rubric,
    document: {
      ...doc,
      question_sets: nextSets,
    },
  };
}

const NEW_QUESTION_SET_BASE = 'New question set';

/** Next unused default name: `New question set`, then `New question set (1)`, `(2)`, … */
function nextDefaultQuestionSetName(existingSets) {
  const names = new Set(
    (existingSets ?? []).map((set) => String(set.name ?? '').trim())
  );
  if (!names.has(NEW_QUESTION_SET_BASE)) {
    return NEW_QUESTION_SET_BASE;
  }
  let k = 1;
  while (names.has(`${NEW_QUESTION_SET_BASE} (${k})`)) {
    k += 1;
  }
  return `${NEW_QUESTION_SET_BASE} (${k})`;
}

export function createRubricQuestionSet(rubric) {
  if (!rubric) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const questionSets = doc.question_sets ?? [];
  const setGuid = generateRubricGuid();
  const nextQuestionSet = {
    guid: setGuid,
    key: `qs-${setGuid}`,
    name: nextDefaultQuestionSetName(questionSets),
    category: '',
    weight: 1,
    questions: [],
  };
  return {
    ...rubric,
    document: {
      ...doc,
      question_sets: [...questionSets, nextQuestionSet],
    },
  };
}

export function removeRubricQuestionSet(rubric, questionSetGuid) {
  if (!rubric) {
    return rubric;
  }

  const doc = rubric.document ?? {};
  const questionSets = doc.question_sets ?? [];
  const nextQuestionSets = questionSets.filter(
    (set) => set.guid !== questionSetGuid
  );

  if (nextQuestionSets.length === questionSets.length) {
    return rubric;
  }

  return {
    ...rubric,
    document: {
      ...doc,
      question_sets: nextQuestionSets,
    },
  };
}

export function removeRubricQuestion(rubric, questionSetGuid, questionGuid) {
  if (!rubric) {
    return rubric;
  }

  const doc = rubric.document ?? {};
  const questionSets = doc.question_sets ?? [];
  const setIndex = questionSets.findIndex(
    (set) => set.guid === questionSetGuid
  );
  if (setIndex === -1) {
    return rubric;
  }

  const set = questionSets[setIndex];
  const questions = set.questions ?? [];
  const nextQuestions = questions.filter(
    (question) => question.guid !== questionGuid
  );
  if (nextQuestions.length === questions.length) {
    return rubric;
  }

  const nextQuestionSets = [...questionSets];
  nextQuestionSets[setIndex] = {
    ...set,
    questions: nextQuestions,
  };

  return {
    ...rubric,
    document: {
      ...doc,
      question_sets: nextQuestionSets,
    },
  };
}

export function addRubricQuestionFromSource(
  rubric,
  targetQuestionSetGuid,
  selectedSource
) {
  return addRubricQuestionsFromSources(
    rubric,
    targetQuestionSetGuid,
    selectedSource ? [selectedSource] : [],
    { includeQuestionSets: false }
  );
}

function normalizeSourceList(selectedSources) {
  if (!Array.isArray(selectedSources)) {
    return [];
  }
  return selectedSources.filter((source) => !!source);
}

function getOrCreateDestinationSetIndex({
  sets,
  targetQuestionSetGuid,
  source,
  includeQuestionSets,
}) {
  if (!includeQuestionSets) {
    return sets.findIndex((set) => set.guid === targetQuestionSetGuid);
  }

  const sourceSetKey = String(source.question_set_key ?? '').trim();
  if (sourceSetKey) {
    const existingByKey = sets.findIndex((set) => {
      const setKey = String(set.key ?? '').trim();
      return setKey.toLowerCase() === sourceSetKey.toLowerCase();
    });
    if (existingByKey !== -1) {
      return existingByKey;
    }
  }

  const createdSetGuid = generateRubricGuid();
  const sourceSetCategory = String(source.question_set_category ?? '').trim();
  sets.push({
    guid: createdSetGuid,
    key: sourceSetKey || `qs-${createdSetGuid}`,
    name: String(source.question_set_name ?? sourceSetKey ?? '').trim(),
    category: sourceSetCategory,
    weight: 1,
    questions: [],
  });
  return sets.length - 1;
}

function createSourcePayloadFromPicker(selectedSource) {
  const sourceGuid = generateRubricGuid();
  const copiedText = selectedSource.question_text ?? '';
  return {
    guid: sourceGuid,
    assessment_key: selectedSource.assessment_key,
    assessment_name: selectedSource.assessment_name,
    question_set_key: selectedSource.question_set_key,
    key: selectedSource.key,
    question_text: copiedText,
  };
}

function createQuestionFromSource(selectedSource) {
  const questionGuid = generateRubricGuid();
  const sourcePayload = createSourcePayloadFromPicker(selectedSource);

  return {
    guid: questionGuid,
    key: selectedSource.key || `q-${questionGuid}`,
    weight: 1,
    text: sourcePayload.question_text,
    sources: [sourcePayload],
  };
}

/**
 * Append one or more assessment question rows as `sources` on an existing rubric question.
 */
export function addRubricSourcesToQuestion(
  rubric,
  questionSetGuid,
  questionGuid,
  selectedSources
) {
  const sourceList = normalizeSourceList(selectedSources);
  if (!rubric || sourceList.length === 0) {
    return rubric;
  }

  const doc = rubric.document ?? {};
  const sets = (doc.question_sets ?? []).map((qs) => ({
    ...qs,
    questions: (qs.questions ?? []).map((q) => ({
      ...q,
      sources: [...(q.sources ?? [])],
    })),
  }));

  const setIdx = sets.findIndex((s) => s.guid === questionSetGuid);
  if (setIdx === -1) {
    return rubric;
  }
  const questions = sets[setIdx].questions;
  const qIdx = questions.findIndex((q) => q.guid === questionGuid);
  if (qIdx === -1) {
    return rubric;
  }

  const nextAssessmentKeys = [...(doc.assessment_keys ?? [])];
  const seenAssessmentKeys = new Set(nextAssessmentKeys);

  const q = questions[qIdx];
  const newSources = [...(q.sources ?? [])];
  for (const selectedSource of sourceList) {
    newSources.push(createSourcePayloadFromPicker(selectedSource));
    const assessmentKey = selectedSource.assessment_key;
    if (assessmentKey && !seenAssessmentKeys.has(assessmentKey)) {
      seenAssessmentKeys.add(assessmentKey);
      nextAssessmentKeys.push(assessmentKey);
    }
  }

  const nextQuestions = [...questions];
  nextQuestions[qIdx] = { ...q, sources: newSources };
  const nextSets = [...sets];
  nextSets[setIdx] = { ...sets[setIdx], questions: nextQuestions };

  return {
    ...rubric,
    document: {
      ...doc,
      question_sets: nextSets,
      assessment_keys: nextAssessmentKeys,
    },
  };
}

export function addRubricQuestionsFromSources(
  rubric,
  targetQuestionSetGuid,
  selectedSources,
  options = {}
) {
  if (!rubric) {
    return rubric;
  }
  const includeQuestionSets = options.includeQuestionSets === true;
  const sourceList = normalizeSourceList(selectedSources);
  if (sourceList.length === 0) {
    return rubric;
  }

  const doc = rubric.document ?? {};
  const sets = [...(doc.question_sets ?? [])];
  if (sets.length === 0) {
    return rubric;
  }
  if (!includeQuestionSets) {
    const setIdx = sets.findIndex((set) => set.guid === targetQuestionSetGuid);
    if (setIdx === -1) {
      return rubric;
    }
  }

  const nextAssessmentKeys = [...(doc.assessment_keys ?? [])];
  const seenAssessmentKeys = new Set(nextAssessmentKeys);

  for (const selectedSource of sourceList) {
    const setIdx = getOrCreateDestinationSetIndex({
      sets,
      targetQuestionSetGuid,
      source: selectedSource,
      includeQuestionSets,
    });
    if (setIdx === -1) {
      continue;
    }
    const targetSet = sets[setIdx];
    const nextQuestion = createQuestionFromSource(selectedSource);
    const nextQuestions = [...(targetSet.questions ?? []), nextQuestion];
    sets[setIdx] = { ...targetSet, questions: nextQuestions };

    const assessmentKey = selectedSource.assessment_key;
    if (assessmentKey && !seenAssessmentKeys.has(assessmentKey)) {
      seenAssessmentKeys.add(assessmentKey);
      nextAssessmentKeys.push(assessmentKey);
    }
  }

  return {
    ...rubric,
    document: {
      ...doc,
      question_sets: sets,
      assessment_keys: nextAssessmentKeys,
    },
  };
}

function updateSourceInDocument(
  doc,
  questionSetGuid,
  questionGuid,
  sourceGuid,
  fieldName,
  rawValue
) {
  const questionSets = doc?.question_sets;
  if (!Array.isArray(questionSets)) {
    return doc;
  }
  const sIdx = questionSets.findIndex((qs) => qs.guid === questionSetGuid);
  if (sIdx === -1) {
    return doc;
  }
  const qs = questionSets[sIdx];
  const questions = qs.questions;
  if (!Array.isArray(questions)) {
    return doc;
  }
  const qIdx = questions.findIndex((q) => q.guid === questionGuid);
  if (qIdx === -1) {
    return doc;
  }
  const q = questions[qIdx];
  const sources = q.sources;
  if (!Array.isArray(sources)) {
    return doc;
  }
  const srcIdx = sources.findIndex((s) => s.guid === sourceGuid);
  if (srcIdx === -1) {
    return doc;
  }
  const nextSources = [...sources];
  nextSources[srcIdx] = { ...nextSources[srcIdx], [fieldName]: rawValue };
  const nextQuestions = [...questions];
  nextQuestions[qIdx] = { ...q, sources: nextSources };
  const nextSets = [...questionSets];
  nextSets[sIdx] = { ...qs, questions: nextQuestions };
  return { ...doc, question_sets: nextSets };
}

export function setRubricQuestionSetRetiredAt(
  rubric,
  questionSetGuid,
  retiredAt
) {
  if (!rubric) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const nextDoc = updateQuestionSetInDocument(
    doc,
    questionSetGuid,
    'retired_at',
    retiredAt
  );
  return { ...rubric, document: nextDoc };
}

export function setRubricQuestionRetiredAt(
  rubric,
  questionSetGuid,
  questionGuid,
  retiredAt
) {
  if (!rubric) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const nextDoc = updateQuestionInDocument(
    doc,
    questionSetGuid,
    questionGuid,
    'retired_at',
    retiredAt
  );
  return { ...rubric, document: nextDoc };
}

export function setRubricSourceRetiredAt(
  rubric,
  questionSetGuid,
  questionGuid,
  sourceGuid,
  retiredAt
) {
  if (!rubric) {
    return rubric;
  }
  const doc = rubric.document ?? {};
  const nextDoc = updateSourceInDocument(
    doc,
    questionSetGuid,
    questionGuid,
    sourceGuid,
    'retired_at',
    retiredAt
  );
  return { ...rubric, document: nextDoc };
}
